import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgreementField } from 'models/Agreement';
import { AgreementPageSize, AgreementPoint } from 'agreementCoordinatesUtils';
import { getIsEditableElement } from 'utils';
import FieldEditorContext from './FieldEditor.context';
import {
  createAgreementField,
  FieldTypeConfig,
  getIsFieldLimitReached,
} from '../../utils';

type FieldEditorProviderProps = {
  fields: AgreementField[];
  invalidFieldIds: string[];
  onChange: (fields: AgreementField[]) => void;
};

const FieldEditorProvider: React.FC<FieldEditorProviderProps> = (props) => {
  const { fields, invalidFieldIds, onChange, children } = props;

  const [selectedFieldId, setSelectedFieldId] = useState<string>();
  const [placingConfig, setPlacingConfig] = useState<FieldTypeConfig>();
  const [isDraggingField, setIsDraggingField] = useState(false);

  const selectedField = useMemo(
    () => fields.find(({ id }) => id === selectedFieldId),
    [fields, selectedFieldId],
  );

  const addField = useCallback(
    (
      page: number,
      center: AgreementPoint,
      pageSize: AgreementPageSize,
      config: FieldTypeConfig = placingConfig,
    ) => {
      if (!config || getIsFieldLimitReached(fields)) return;

      const field = createAgreementField(config, page, center, pageSize);

      onChange([...fields, field]);
      setSelectedFieldId(field.id);
      setPlacingConfig(undefined);
    },
    [fields, onChange, placingConfig],
  );

  const updateField = useCallback(
    (field: AgreementField) =>
      onChange(fields.map((item) => (item.id === field.id ? field : item))),
    [fields, onChange],
  );

  const removeField = useCallback(
    (fieldId: string) => {
      onChange(fields.filter(({ id }) => id !== fieldId));
      setSelectedFieldId(undefined);
    },
    [fields, onChange],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (getIsEditableElement(event.target)) return;

      if (event.key === 'Escape') {
        setPlacingConfig(undefined);
        setSelectedFieldId(undefined);
        return;
      }

      if (
        selectedFieldId &&
        (event.key === 'Delete' || event.key === 'Backspace')
      )
        removeField(selectedFieldId);
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [removeField, selectedFieldId]);

  return (
    <FieldEditorContext.Provider
      value={{
        fields,
        selectedField,
        placingConfig,
        invalidFieldIds,
        isDraggingField,
        setSelectedFieldId,
        setPlacingConfig,
        setIsDraggingField,
        addField,
        updateField,
        removeField,
      }}
    >
      {children}
    </FieldEditorContext.Provider>
  );
};

export default FieldEditorProvider;
